import React, { useState, useEffect, useCallback } from 'react';
import { Star } from '@phosphor-icons/react';
import { Spinner, StatCard, EmptyState } from './Shared';

export default function RatingsTab({ api }) {
  const [ratings, setRatings] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const [rRes, iRes] = await Promise.all([
        api.get('/meals/ratings?limit=30'),
        api.get('/meals/ratings/insights'),
      ]);
      setRatings(rRes.data);
      setInsights(iRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {insights && insights.total_ratings > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <StatCard label="Total Ratings" value={insights.total_ratings} color="#FF6B35" />
          <StatCard label="Avg Rating" value={`${insights.avg_rating}/5`} color="#22C55E" />
          <StatCard label="Top Dishes" value={insights.top_dishes?.length || 0} color="#00E5FF" />
          <StatCard label="Avoid" value={insights.avoid_dishes?.length || 0} color="#EF4444" />
        </div>
      )}

      {insights && insights.top_dishes?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <p className="text-xs font-semibold text-green-400 mb-2">Family Favorites</p>
            <div className="space-y-1.5">
              {insights.top_dishes.slice(0, 6).map(d => (
                <div key={d.name} className="flex items-center justify-between text-sm">
                  <span>{d.name}</span>
                  <span className="text-xs text-green-400 flex items-center gap-1"><Star size={10} weight="fill" /> {d.avg}</span>
                </div>
              ))}
            </div>
          </div>
          {insights.make_again?.length > 0 && (
            <div className="rounded-2xl p-4" style={{ background: 'rgba(255,107,53,0.05)', border: '1px solid rgba(255,107,53,0.15)' }}>
              <p className="text-xs font-semibold text-[#FF6B35] mb-2">Make Again Requests</p>
              <div className="flex flex-wrap gap-1.5">
                {insights.make_again.map(d => (
                  <span key={d} className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#FF6B35]/10 text-[#FF6B35]">{d}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-neutral-400 mb-2">Recent Ratings</p>
        {ratings.length === 0 ? (
          <EmptyState text="No ratings yet. Rate dishes from the This Week tab to train the AI." />
        ) : (
          <div className="space-y-2">
            {ratings.map(r => (
              <div key={r.rating_id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <Star size={14} weight="fill" className="text-yellow-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold truncate">{r.dish_name}</p>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={10} weight={s <= r.rating ? 'fill' : 'regular'} className={s <= r.rating ? 'text-yellow-400' : 'text-neutral-600'} />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-0.5 flex-wrap">
                    <span>{r.date}</span>
                    <span>{r.meal_type}</span>
                    {r.tags?.map(t => <span key={t} className="px-1.5 py-0.5 rounded bg-white/5 text-neutral-400">{t.replace('_', ' ')}</span>)}
                  </div>
                  {r.notes && <p className="text-[11px] text-neutral-500 mt-0.5">{r.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
